/**
 * Events for the old-commune row of the Viettel Post booking form: whether
 * sales see it, and whether they confirm or change the commune the system
 * suggested. One event name, the step in `action`. Contract:
 * `docs/analytics/carrier-legacy-commune.md`.
 *
 * Only enums, counts and booleans leave the browser. The commune names, the
 * ward, the order and the customer stay out: which commune was sent, who sent
 * it and whether it differed from the suggestion is recorded by the server on
 * the shipment, which is where this telemetry is joined to the delivery
 * outcome.
 */
import { getAnalyticsActor, posthog } from './posthog'

const actions = ['shown', 'changed', 'confirmed', 'blocked'] as const
const sources = ['single', 'previous_order', 'address', 'default'] as const
const reasons = [
    'unconfirmed',
    'updating',
    'required',
    'not_candidate',
] as const

export type CarrierLegacyCommuneAction = (typeof actions)[number]
/** Why the server suggested the commune; mirrors the legacy-units response. */
export type CarrierLegacyCommuneSource = (typeof sources)[number]
/**
 * Why a submit did not go out: `unconfirmed` the guess was never confirmed,
 * `updating` the suggestion was still refreshing for an edited address,
 * `required` / `not_candidate` the server refused with that 422.
 */
export type CarrierLegacyCommuneBlock = (typeof reasons)[number]

export interface CarrierLegacyCommuneEvent {
    action: CarrierLegacyCommuneAction
    /** The suggestion in front of the operator when the step happened. */
    source?: CarrierLegacyCommuneSource | null
    candidateCount?: number
    otherCount?: number
    /** `changed`: the commune picked comes from the "Xã khác" group. */
    toOtherList?: boolean
    /** `changed`: the commune picked is the suggested one again. */
    toSuggestion?: boolean
    /** `changed`: the commune was found by search in another ward, which
     *  moved the receiver to that ward. */
    toOtherWard?: boolean
    /** `shown`: the row warned that the address names another ward. */
    addressWard?: boolean
    /** `changed`: the move came from that warning's button. */
    fromAddressWard?: boolean
    reason?: CarrierLegacyCommuneBlock
}

const count = (value: number | undefined) =>
    value !== undefined && Number.isInteger(value) && value >= 0 ? value : null

/** Analytics failure must never block a booking. */
export function captureCarrierLegacyCommune(event: CarrierLegacyCommuneEvent) {
    try {
        const actor = getAnalyticsActor()
        if (!actor) return
        if (!actions.includes(event.action)) return
        const candidates = count(event.candidateCount)
        const others = count(event.otherCount)
        posthog.capture('carrier_legacy_commune', {
            schema_version: 1,
            event_id: crypto.randomUUID(),
            ...actor,
            action: event.action,
            ...(event.source && sources.includes(event.source)
                ? { suggestion_source: event.source }
                : {}),
            ...(candidates !== null ? { candidate_count: candidates } : {}),
            ...(others !== null ? { other_count: others } : {}),
            ...(typeof event.toOtherList === 'boolean'
                ? { to_other_list: event.toOtherList }
                : {}),
            ...(typeof event.toSuggestion === 'boolean'
                ? { to_suggestion: event.toSuggestion }
                : {}),
            ...(typeof event.toOtherWard === 'boolean'
                ? { to_other_ward: event.toOtherWard }
                : {}),
            ...(typeof event.addressWard === 'boolean'
                ? { address_ward: event.addressWard }
                : {}),
            ...(typeof event.fromAddressWard === 'boolean'
                ? { from_address_ward: event.fromAddressWard }
                : {}),
            ...(event.reason && reasons.includes(event.reason)
                ? { reason: event.reason }
                : {}),
        })
    } catch {
        // Analytics failure must not block the booking.
    }
}
